import { Box, Stack, Typography, useTheme } from "@mui/material";
import TouchRipple from "@mui/material/ButtonBase/TouchRipple";
import { Avatar, Tooltip } from "components";
import { useGlobalStore } from "controller";
import dayjs from "dayjs";
import React from "react";
import { useNavigate } from "react-router-dom";
import { ChatNotificationSchema } from "shared/types";
import { isoToDateAndTime } from "utilz/helpers";
import { stripHtml } from "utilz/helpers/stripHtml";

export const MessageListCard = ({
  chatData,
  onClick,
  isSelected,
  onClose,
}: {
  isSelected: boolean;
  chatData: ChatNotificationSchema;
  onClick: () => void;
  onClose: () => void;
}) => {
  const theme = useTheme();
  const timezone = useGlobalStore((state) => state.timezone);
  const navigate = useNavigate();

  return (
    <Stack
      onClick={onClick}
      direction={"row"}
      height={"104px"}
      sx={{
        backgroundPosition: "center",
        transition: "background 0.8s",
        cursor: "pointer",
        width: "100%",
        p: "12px 24px",
        pr: "19px",
        gap: "16px",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        bgcolor: isSelected ? theme.palette.neutral["005"] : "white",
        borderRight: `5px solid ${
          isSelected ? theme.palette.primary.main : "transparent"
        }`,
        "&:hover": {
          background: `${theme.palette.neutral["005"]} radial-gradient(circle, transparent 1%, ${theme.palette.neutral["B-005"]} 1%) center/15000%`,
        },
        "&:active": {
          backgroundColor: theme.palette.neutral["005"],
          backgroundSize: "100%",
          transition: "background 0s",
        },
      }}
    >
      <Avatar
        variants="md"
        fullname={chatData.notification?.sender_details?.display_name}
        url={chatData.notification?.sender_details?.profile_pic}
      />
      <Stack
        sx={{
          flexGrow: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          gap: "4px",
        }}
      >
        <Typography
          variant="subtitle1"
          noWrap
          color={theme.palette.lightBg.high}
        >
          {chatData.notification?.sender_details.display_name}
        </Typography>
        {/* <Typography
          variant="caption-2"
          noWrap
          color={theme.palette.lightBg.medium}
          sx={{
            textDecoration: "underline",
            color: theme.palette.primary.main,
          }}
          onClick={(e) => {
            e.stopPropagation();
            onFullScreenButtonClick();
            e.preventDefault();
          }}
        >
          {chatData.u_id}
        </Typography> */}
        <Tooltip title={chatData.notification?.message} followCursor={true}>
          <Typography variant="body-medium" noWrap>
            {stripHtml(chatData.notification?.message || "New attachment")}
          </Typography>
        </Tooltip>

        <Stack direction={"row"} alignItems={"center"} gap={"4px"}>
          <Typography
            variant="body-small"
            // noWrap
            color={theme.palette.lightBg.low}
          >
            {chatData.u_id}
          </Typography>
          <Box
            component={"span"}
            sx={{
              width: "4px",
              height: "4px",
              bgcolor: theme.palette.lightBg.low,
              aspectRatio: "1/1",
              // px: "4px",
              borderRadius: "100%",
            }}
          ></Box>
          <Tooltip title={chatData.service_summary} followCursor={true}>
            <Typography
              variant="body-small"
              noWrap
              color={theme.palette.lightBg.low}
            >
              {chatData.service_summary}
            </Typography>
          </Tooltip>
        </Stack>
      </Stack>
      <Stack
        sx={{
          width: "fit-content",
          height: "100%",
          justifyContent: "space-between",
          alignItems: "end",
        }}
      >
        <Typography
          variant="body-small"
          noWrap
          color={theme.palette.lightBg.low}
        >
          {isoToDateAndTime({
            isodate: chatData?.notification?.created_at || "",
            timezone,
            format: dayjs(chatData?.notification?.created_at || "").isToday()
              ? "hh:mm A"
              : "MMM DD",
          })}
        </Typography>

        <Box
          sx={{
            backgroundColor: "red",
            borderRadius: "22px",
            width: "fit-content",
            height: "22px",
            minWidth: "22px",
            px: "2px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            visibility: chatData.unseenCount > 0 ? "visible" : "hidden",
          }}
        >
          <Typography variant="caption-xs" color="white">
            {chatData.unseenCount < 100 ? chatData.unseenCount : "99+"}
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
};
